import { useQuery } from '@tanstack/react-query'
import { SEARCH_QUERY_MIN_LENGTH } from 'constants/index'
import {
  findCollection,
  useCollectionRegistry
} from 'hooks/useCollectionRegistry'
import { useCollectionTokenItem } from 'hooks/useItem'
import { useMemo } from 'react'
import { SearchableCollection } from 'state/search/reducer'
import { Chain, CollectionVerificationStatus, ItemDetail } from 'types/nft'
import { shortenAddress } from 'utils'
import { convertChainToChainId } from 'utils/chain'
import {
  parseSearchQuery,
  searchCollections,
  toSearchableCollection
} from 'utils/search'
import { getAddress } from 'viem'
import { usePublicClient } from 'wagmi'

const SEARCH_CHAIN: Chain = 'avalanche'

export interface SearchProfile {
  address: string
}

export type SearchItem = Pick<
  ItemDetail,
  | 'chain'
  | 'collection'
  | 'collectionName'
  | 'metadata'
  | 'tokenId'
  | 'tokenUri'
  | 'verified'
>

interface SearchResults {
  collections: SearchableCollection[]
  items: SearchItem[]
  profiles: SearchProfile[]
}

const useHasContractCode = (address: string | undefined, enabled: boolean) => {
  const chainId = convertChainToChainId(SEARCH_CHAIN)
  const publicClient = usePublicClient({ chainId })
  const isEnabled = enabled && address !== undefined
  return useQuery(
    ['contractCode', chainId, address],
    async () => {
      if (address === undefined) {
        return false
      }
      const bytecode = await publicClient.getBytecode({
        address: getAddress(address)
      })
      return bytecode !== undefined && bytecode !== '0x'
    },
    { enabled: isEnabled, staleTime: Infinity }
  )
}

const toFallbackItem = (
  address: string,
  tokenId: string,
  collection: SearchableCollection | undefined
): SearchItem => ({
  chain: SEARCH_CHAIN,
  collection: address,
  collectionName: collection?.name ?? shortenAddress(address),
  metadata: null,
  tokenId,
  tokenUri: null,
  verified: collection?.verified ?? CollectionVerificationStatus.Unverified
})

export const useSearch = (
  query: string
): { data: SearchResults | undefined; isLoading: boolean } => {
  const { address, text, tokenId } = parseSearchQuery(query)
  const isActive = text.length >= SEARCH_QUERY_MIN_LENGTH
  const { data: registry, isLoading: isLoadingRegistry } =
    useCollectionRegistry()

  const collections = useMemo(
    () => (isActive && registry ? searchCollections(registry, text) : []),
    [isActive, registry, text]
  )
  const registryCollection = useMemo(() => {
    if (!registry || address === undefined) {
      return undefined
    }
    const collection = findCollection(registry, address, SEARCH_CHAIN)
    return collection ? toSearchableCollection(collection) : undefined
  }, [address, registry])

  const {
    data: itemDetail,
    isError: isItemError,
    isInitialLoading: isLoadingItem
  } = useCollectionTokenItem(address, tokenId, SEARCH_CHAIN, { silent: true })

  const { data: hasContractCode, isInitialLoading: isLoadingContractCode } =
    useHasContractCode(
      address,
      !isLoadingRegistry && tokenId === undefined && !registryCollection
    )

  const data = useMemo((): SearchResults | undefined => {
    if (!isActive) {
      return undefined
    }
    if (address === undefined) {
      return { collections, items: [], profiles: [] }
    }
    if (tokenId !== undefined) {
      const item =
        itemDetail ??
        (isItemError
          ? toFallbackItem(address, tokenId, registryCollection)
          : undefined)
      return { collections: [], items: item ? [item] : [], profiles: [] }
    }
    const unknownContract: SearchableCollection[] =
      !registryCollection && hasContractCode
        ? [
            {
              address,
              chain: SEARCH_CHAIN,
              name: shortenAddress(address),
              verified: CollectionVerificationStatus.Unverified
            }
          ]
        : []
    return {
      collections: [...collections, ...unknownContract],
      items: [],
      profiles: [{ address }]
    }
  }, [
    address,
    collections,
    hasContractCode,
    isActive,
    isItemError,
    itemDetail,
    registryCollection,
    tokenId
  ])

  return {
    data,
    isLoading:
      isActive && (isLoadingRegistry || isLoadingItem || isLoadingContractCode)
  }
}
